import {
  BoltIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@reader-tracker/shared/components/ui/button'
import { Card } from '@reader-tracker/shared/components/ui/card'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import React, { useState } from 'react'

interface RunResult {
  ok: boolean
  data?: unknown
  error?: string
}

interface Source {
  id: string
  name: string
}

interface FunctionStatus {
  loading: boolean
  result: RunResult | null
}

type StatusMap = Record<string, FunctionStatus>

const EDGE_FUNCTIONS = [
  {
    name: 'health-check',
    label: 'Health Check',
    description:
      'Checks the health status of the Supabase Edge Function runtime.',
    auto: true,
  },
  {
    name: 'sync-chapters',
    label: 'Sync Chapters',
    description:
      'Fetches chapter listings from supported sources and upserts new chapters into the database.',
  },
  {
    name: 'fanout-notifications',
    label: 'Fanout Notifications',
    description:
      'Fans out notifications to all followers for every stored chapter. Run after Sync Chapters.',
  },
  {
    name: 'fetch-title-thumbnails',
    label: 'Fetch Title Thumbnails',
    description: 'Fetches and uploads missing title thumbnails to R2 storage.',
  },
]

export default function EdgeFunctionsPage() {
  const [statusMap, setStatusMap] = useState<StatusMap>(() =>
    Object.fromEntries(
      EDGE_FUNCTIONS.map((fn) => [fn.name, { loading: false, result: null }]),
    ),
  )
  const [sources, setSources] = useState<Source[]>([])
  const [syncSourceId, setSyncSourceId] = useState<string>('all')

  // Fetch sources for sync-chapters dropdown
  React.useEffect(() => {
    supabase
      .from('sources')
      .select('id, name')
      .order('name')
      .then(({ data }) => {
        if (data) setSources(data)
      })
  }, [])

  // Auto-call health-check on mount
  React.useEffect(() => {
    const healthFn = EDGE_FUNCTIONS.find((fn) => fn.name === 'health-check')
    if (!healthFn) return
    setStatus(healthFn.name, { loading: true, result: null })
    supabase.functions
      .invoke(healthFn.name, {
        body: {},
      })
      .then(({ data, error }) => {
        if (error) {
          setStatus(healthFn.name, {
            loading: false,
            result: { ok: false, error: error.message },
          })
        } else {
          setStatus(healthFn.name, {
            loading: false,
            result: { ok: true, data },
          })
        }
      })
  }, [])

  const setStatus = (name: string, patch: Partial<FunctionStatus>) => {
    setStatusMap((prev) => ({
      ...prev,
      [name]: { ...prev[name], ...patch },
    }))
  }

  const handleRun = async (fnName: string) => {
    setStatus(fnName, { loading: true, result: null })

    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session) {
      setStatus(fnName, {
        loading: false,
        result: { ok: false, error: 'Not authenticated. Please log in.' },
      })
      return
    }

    const invokeBody =
      fnName === 'sync-chapters' && syncSourceId !== 'all'
        ? { source_id: syncSourceId }
        : {}

    const { data, error } = await supabase.functions.invoke(fnName, {
      body: invokeBody,
      headers: { Authorization: `Bearer ${session.access_token}` },
    })

    if (error) {
      setStatus(fnName, {
        loading: false,
        result: { ok: false, error: error.message },
      })
      return
    }

    setStatus(fnName, {
      loading: false,
      result: { ok: true, data },
    })
  }

  const handleClear = (fnName: string) => {
    setStatus(fnName, { result: null })
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-bold text-2xl text-gray-900">Edge Functions</h1>
        <p className="mt-1 text-gray-500 text-sm">
          Manually trigger background jobs. Results are shown inline.
        </p>
      </div>

      <div className="space-y-4">
        {/* Health Check status card (auto-called) */}
        {(() => {
          const fn = EDGE_FUNCTIONS.find((f) => f.name === 'health-check')
          if (!fn) return null
          const status = statusMap[fn.name]
          return (
            <Card key={fn.name} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <BoltIcon className="h-5 w-5 shrink-0 text-blue-600" />
                    <h2 className="font-semibold text-gray-900">{fn.label}</h2>
                    <code className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-500 text-xs">
                      {fn.name}
                    </code>
                  </div>
                  <p className="mt-1 text-gray-500 text-sm">{fn.description}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {status.loading && (
                    <span className="text-gray-500 text-xs">Checking…</span>
                  )}
                  {status.result && (
                    <button
                      type="button"
                      onClick={() => handleClear(fn.name)}
                      className="text-gray-400 text-xs underline hover:text-gray-600"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {/* Result banner */}
              {status.result && (
                <div
                  className={`mt-4 rounded-md border px-4 py-3 ${
                    status.result.ok
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {status.result.ok ? (
                      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                    ) : (
                      <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className={`font-medium text-sm ${
                          status.result.ok ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {status.result.ok ? 'Healthy' : 'Error'}
                      </p>
                      {status.result.error && (
                        <p className="mt-0.5 text-red-700 text-sm">
                          {status.result.error}
                        </p>
                      )}
                      {status.result.ok && status.result.data != null && (
                        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-green-700 text-xs">
                          {JSON.stringify(status.result.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )
        })()}

        {/* Other edge functions (manual trigger) */}
        {EDGE_FUNCTIONS.filter((fn) => fn.name !== 'health-check').map((fn) => {
          const status = statusMap[fn.name]
          return (
            <Card key={fn.name} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <BoltIcon className="h-5 w-5 shrink-0 text-blue-600" />
                    <h2 className="font-semibold text-gray-900">{fn.label}</h2>
                    <code className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-500 text-xs">
                      {fn.name}
                    </code>
                  </div>
                  <p className="mt-1 text-gray-500 text-sm">{fn.description}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {fn.name === 'sync-chapters' && (
                    <select
                      aria-label="Source to sync"
                      value={syncSourceId}
                      onChange={(e) => setSyncSourceId(e.target.value)}
                      disabled={status.loading}
                      className="rounded border border-gray-300 px-2 py-1 text-sm"
                    >
                      <option value="all">All Sources</option>
                      {sources.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {status.result && (
                    <button
                      type="button"
                      onClick={() => handleClear(fn.name)}
                      className="text-gray-400 text-xs underline hover:text-gray-600"
                    >
                      Clear
                    </button>
                  )}
                  <Button
                    onClick={() => handleRun(fn.name)}
                    disabled={status.loading}
                    size="sm"
                  >
                    {status.loading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Running…
                      </span>
                    ) : (
                      'Run'
                    )}
                  </Button>
                </div>
              </div>
              {/* Result banner */}
              {status.result && (
                <div
                  className={`mt-4 rounded-md border px-4 py-3 ${
                    status.result.ok
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {status.result.ok ? (
                      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                    ) : (
                      <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className={`font-medium text-sm ${
                          status.result.ok ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {status.result.ok ? 'Success' : 'Error'}
                      </p>
                      {status.result.error && (
                        <p className="mt-0.5 text-red-700 text-sm">
                          {status.result.error}
                        </p>
                      )}
                      {status.result.ok && status.result.data != null && (
                        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-green-700 text-xs">
                          {JSON.stringify(status.result.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}
